import React, { useState, useEffect, useRef, useMemo } from 'react';
import SectionAssistantButton from './SectionAssistantButton';
import {
  Box,
  Paper,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  Collapse,
  Fade,
  Zoom,
  useTheme,
  useMediaQuery,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ContentCopy as CopyIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  CheckCircle as CheckIcon,
  RadioButtonUnchecked as EmptyIcon,
  Bookmark as BookmarkIcon,
  BookmarkBorder as BookmarkBorderIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';
import PublishedSoaView from './PublishedSoaView';
import { processContentForDisplay, finalizeGeneratedProtocolOutput, computeMediaNumberOffsets } from '../../utils/formattingUtils';
import mermaid from 'mermaid';
import { sanitizeMermaidSvg } from '../../utils/sanitizeMermaidSvg';
import { sanitizeRichHtml } from '../../utils/sanitizeRichHtml';

if (typeof window !== 'undefined' && !window.__protocolMermaidInit) {
  mermaid.initialize({
    startOnLoad: false,
    theme: 'default',
    securityLevel: 'strict',
    flowchart: { htmlLabels: false, curve: 'basis' },
  });
  window.__protocolMermaidInit = true;
}

/** Renders protocol HTML and hydrates any embedded Mermaid flowcharts. */
function ProtocolHtmlContent({ html, sx }) {
  const containerRef = useRef(null);

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return undefined;

    let cancelled = false;

    const renderMermaidBlocks = async () => {
      const nodes = root.querySelectorAll('pre.mermaid, pre.liver-event-mermaid');
      for (let i = 0; i < nodes.length; i += 1) {
        const node = nodes[i];
        if (node.getAttribute('data-rendered') === '1') continue;
        const source = (node.textContent || '').trim();
        if (!source) continue;
        try {
          const id = `protocol-mermaid-${Date.now()}-${i}`;
          const { svg } = await mermaid.render(id, source);
          if (cancelled) return;
          const wrap = document.createElement('div');
          wrap.className = 'protocol-mermaid-svg';
          wrap.innerHTML = sanitizeMermaidSvg(svg);
          node.setAttribute('data-rendered', '1');
          node.replaceWith(wrap);
        } catch (err) {
          console.error('Protocol Mermaid render failed:', err);
          node.setAttribute('data-rendered', 'error');
        }
      }
    };

    renderMermaidBlocks();
    return () => {
      cancelled = true;
    };
  }, [html]);

  return (
    <Box
      ref={containerRef}
      sx={sx}
      dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(html) }}
    />
  );
}

// We'll use dangerouslySetInnerHTML for HTML content instead of rehype-raw
// This avoids the dependency and provides better control over styling

const DocumentCanvas = ({ 
  sections, 
  mode, 
  onSectionClick, 
  onCopyContent, 
  onToggleBookmark,
  onUpdateSection,
  bookmarkedSections = new Set(),
  showSectionNumbers = true,
  enableAnimations = true,
  trialSchemaImageUrl = null,
  generatedSoAData = null,
  s9SoaTables = [],
  s9PublishedVersion = null,
  actualProjectId = null,
  handleSoADataUpdate = null,
  handleRegenerateSoA = null,
  /** When true (e.g. workflow document approved), hide section edit / AI affordances */
  readonlyContent = false,
}) => {

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // Table/Figure numbering continues across sections sharing a main number (1-1, 1-2, …).
  const mediaNumberOffsets = useMemo(() => computeMediaNumberOffsets(sections || []), [sections]);
  const [expandedSections, setExpandedSections] = useState(new Set());
  const [copiedSectionId, setCopiedSectionId] = useState(null);
  const [editingSectionId, setEditingSectionId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [editPrompt, setEditPrompt] = useState('');
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const sectionRefs = useRef({});

  // Auto-expand sections with content (include SOA/schema when external data exists)
  useEffect(() => {
    const sectionsWithContent = sections
      .filter(section => {
        if (section.title === '1.2 TRIAL SCHEMA' && trialSchemaImageUrl) return true;
        if (
          section.title === '1.3 SCHEDULE OF ACTIVITIES' &&
          (generatedSoAData || (Array.isArray(s9SoaTables) && s9SoaTables.length > 0))
        ) {
          return true;
        }
        return section.output && section.output.trim();
      })
      .map(section => section._id);
    setExpandedSections(new Set(sectionsWithContent));
  }, [sections, generatedSoAData, s9SoaTables, trialSchemaImageUrl]);

  const handleSectionToggle = (sectionId) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  const handleCopyContent = async (sectionId, content) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedSectionId(sectionId);
      setTimeout(() => setCopiedSectionId(null), 2000);
      onCopyContent?.(content);
      showSnackbarMessage('Content copied to clipboard!', 'success');
    } catch (err) {
      console.error('Failed to copy content:', err);
      showSnackbarMessage('Failed to copy content', 'error');
    }
  };

  const handleEditSection = (section) => {
    if (readonlyContent) return;
    setEditingSectionId(section._id);
    setEditContent(section.output || '');
    setEditPrompt(section.prompt || '');
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    try {
      if (onUpdateSection) {
        await onUpdateSection(editingSectionId, {
          output: editContent,
          prompt: editPrompt
        });
        showSnackbarMessage('Section updated successfully!', 'success');
      }
      setShowEditDialog(false);
      setEditingSectionId(null);
      setEditContent('');
      setEditPrompt('');
    } catch (error) {
      console.error('Failed to update section:', error);
      showSnackbarMessage('Failed to update section', 'error');
    }
  };

  const handleCancelEdit = () => {
    setShowEditDialog(false);
    setEditingSectionId(null);
    setEditContent('');
    setEditPrompt('');
  };

  const showSnackbarMessage = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setShowSnackbar(true);
  };

  const getSectionStatus = (section) => {
    const hasSoa =
      section.title === '1.3 SCHEDULE OF ACTIVITIES' &&
      ((Array.isArray(s9SoaTables) && s9SoaTables.length > 0) ||
        generatedSoAData?.visits ||
        generatedSoAData?.sections);
    const hasSchema =
      section.title === '1.2 TRIAL SCHEMA' && Boolean(trialSchemaImageUrl);
    if ((!section.output || !section.output.trim()) && !hasSoa && !hasSchema) {
      return { icon: <EmptyIcon />, color: 'default', label: 'Empty' };
    }
    if (section.final) {
      return { icon: <CheckIcon />, color: 'success', label: 'Final' };
    }
    return { icon: <ViewIcon />, color: 'primary', label: 'Draft' };
  };

  const renderMarkdownContent = (content, sectionTitle = '', mediaNumbering = undefined) => {
    // Process content to convert asterisks to proper HTML formatting
    const finalized = finalizeGeneratedProtocolOutput(content, sectionTitle, mediaNumbering);
    const processedContent = processContentForDisplay(finalized);
    
    // Check if content contains HTML tags
    const hasHTML = processedContent.includes('<') && processedContent.includes('>');
    
         // For HTML content, use dangerouslySetInnerHTML for better control and to avoid rehype-raw issues
     if (hasHTML) {
       return (
         <ProtocolHtmlContent
           html={processedContent}
           sx={{
             fontFamily: 'Times New Roman, serif',
             fontSize: '14px',
             lineHeight: 1.6,
             color: '#333',
             '& h1': {
               fontWeight: 'bold',
               color: '#2c3e50',
               mt: 1.5,
               mb: 1,
               borderBottom: '2px solid #3498db',
               pb: 1,
               fontSize: '1.5rem'
             },
             '& h2': {
               fontWeight: 'bold',
               color: '#2c3e50',
               mt: 1,
               mb: 0.75,
               fontSize: '1.25rem'
             },
             '& h3': {
               fontWeight: 'bold',
               color: '#2c3e50',
               mt: 0.75,
               mb: 0.5,
               fontSize: '1.1rem'
             },
             '& h4': {
               fontWeight: 'bold',
               color: '#2c3e50',
               mt: 0.5,
               mb: 0.5,
               fontSize: '1rem'
             },
             '& strong': {
               fontWeight: 'bold',
               color: '#2c3e50'
             },
             '& em': {
               fontStyle: 'italic',
               color: '#666'
             },
             '& ul, & ol': {
               mb: 2,
               pl: 3,
               my: 1
             },
             '& li': {
               mb: 0.5,
               lineHeight: 1.6
             },
             '& p': {
               mb: 1,
               lineHeight: 1.6,
               color: '#333'
             },
             '& table': {
               borderCollapse: 'collapse',
               width: '100%',
               border: '1px solid #e0e0e0',
               borderRadius: '4px',
               mb: 2
             },
             '& th, & td': {
               border: '1px solid #e0e0e0',
               padding: '12px',
               textAlign: 'left'
             },
             '& th': {
               backgroundColor: '#f8f9fa',
               fontWeight: 'bold'
             },
             '& blockquote': {
               borderLeft: '4px solid #3498db',
               pl: 2,
               ml: 2,
               my: 2,
               fontStyle: 'italic',
               color: '#666'
             },
             '& .protocol-mermaid-svg': {
               display: 'flex',
               justifyContent: 'center',
               my: 2,
               overflow: 'auto'
             }
           }}
         />
       );
     }
    
         // For non-HTML content, use ReactMarkdown
     return (
       <ReactMarkdown
         remarkPlugins={[remarkGfm]}
         components={{
           // Consistent styling for all HTML elements
           h1: ({ children }) => (
             <Typography variant="h4" sx={{ 
               fontWeight: 'bold', 
               color: '#2c3e50',
               mt: 1.5,
               mb: 1,
               borderBottom: '2px solid #3498db',
               pb: 1
             }}>
               {children}
             </Typography>
           ),
           h2: ({ children }) => (
             <Typography variant="h5" sx={{ 
               fontWeight: 'bold', 
               color: '#2c3e50',
               mt: 1,
               mb: 0.75
             }}>
               {children}
             </Typography>
           ),
           h3: ({ children }) => (
             <Typography variant="h6" sx={{ 
               fontWeight: 'bold', 
               color: '#2c3e50',
               mt: 0.75,
               mb: 0.5
             }}>
               {children}
             </Typography>
           ),
           h4: ({ children }) => (
             <Typography variant="subtitle1" sx={{ 
               fontWeight: 'bold', 
               color: '#2c3e50',
               mt: 0.5,
               mb: 0.5
             }}>
               {children}
             </Typography>
           ),
           strong: ({ children }) => (
             <Typography component="span" sx={{ 
               fontWeight: 'bold', 
               color: '#2c3e50'
             }}>
               {children}
             </Typography>
           ),
           em: ({ children }) => (
             <Typography component="span" sx={{ 
               fontStyle: 'italic', 
               color: '#666'
             }}>
               {children}
             </Typography>
           ),
           p: ({ children }) => (
             <Typography sx={{ 
               mb: 1, 
               lineHeight: 1.6,
               color: '#333'
             }}>
               {children}
             </Typography>
           ),
           ul: ({ children }) => (
             <Box component="ul" sx={{ 
               pl: 3, 
               my: 1,
               mb: 2
             }}>
               {children}
             </Box>
           ),
           ol: ({ children }) => (
             <Box component="ol" sx={{ 
               pl: 3, 
               my: 1,
               mb: 2
             }}>
               {children}
             </Box>
           ),
           li: ({ children }) => (
             <Box component="li" sx={{ 
               mb: 0.5,
               lineHeight: 1.6
             }}>
               {children}
             </Box>
           ),
           code({ node, inline, className, children, ...props }) {
             const match = /language-(\w+)/.exec(className || '');
             return !inline && match ? (
               <SyntaxHighlighter
                 style={tomorrow}
                 language={match[1]}
                 PreTag="div"
                 {...props}
               >
                 {String(children).replace(/\n$/, '')}
               </SyntaxHighlighter>
             ) : (
               <code className={className} {...props}>
                 {children}
               </code>
             );
           },
           table: ({ children }) => (
             <Box sx={{ overflowX: 'auto', my: 2 }}>
               <table style={{
                 borderCollapse: 'collapse',
                 width: '100%',
                 border: '1px solid #e0e0e0',
                 borderRadius: '4px'
               }}>
                 {children}
               </table>
             </Box>
           ),
           th: ({ children }) => (
             <th style={{
               border: '1px solid #e0e0e0',
               padding: '12px',
               backgroundColor: '#f8f9fa',
               fontWeight: 'bold',
               textAlign: 'left'
             }}>
               {children}
             </th>
           ),
           td: ({ children }) => (
             <td style={{
               border: '1px solid #e0e0e0',
               padding: '12px'
             }}>
               {children}
             </td>
           ),
           blockquote: ({ children }) => (
             <Box sx={{
               borderLeft: '4px solid #3498db',
               pl: 2,
               ml: 2,
               my: 2,
               fontStyle: 'italic',
               color: '#666'
             }}>
               {children}
             </Box>
           )
         }}
       >
         {processedContent}
       </ReactMarkdown>
     );
  };

  const renderSection = (section, index) => {
    const isExpanded = expandedSections.has(section._id);
    const hasSoaData =
      section.title === '1.3 SCHEDULE OF ACTIVITIES' &&
      ((Array.isArray(s9SoaTables) && s9SoaTables.length > 0) ||
        Boolean(generatedSoAData?.visits || generatedSoAData?.sections || generatedSoAData));
    const hasSchemaData =
      section.title === '1.2 TRIAL SCHEMA' && Boolean(trialSchemaImageUrl);
    const hasContent =
      (section.output && section.output.trim()) || hasSoaData || hasSchemaData;
    const status = getSectionStatus(section);
    const isBookmarked = bookmarkedSections.has(section._id);
    const isCopied = copiedSectionId === section._id;
    const isEditing = editingSectionId === section._id;

    const SectionWrapper = enableAnimations ? Fade : Box;

    // Special handling for Trial Schema and Schedule of Activities
    const renderSpecialSectionContent = () => {
      if (section.title === '1.2 TRIAL SCHEMA') {
        return (
          <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 500 }}>
              Trial Schema Image
            </Typography>
            {trialSchemaImageUrl ? (
              <Box sx={{ textAlign: 'center' }}>
                {trialSchemaImageUrl.trim().startsWith('<svg') ? (
                  <img
                    src={`data:image/svg+xml;utf8,${encodeURIComponent(trialSchemaImageUrl)}`}
                    alt="Trial Schema"
                    style={{ 
                      maxWidth: '100%', 
                      maxHeight: 300, 
                      border: '1px solid #ccc', 
                      borderRadius: 8,
                      cursor: 'pointer'
                    }}
                  />
                ) : (
                  <img
                    src={trialSchemaImageUrl}
                    alt="Trial Schema"
                    style={{ 
                      maxWidth: '100%', 
                      maxHeight: 300, 
                      border: '1px solid #ccc', 
                      borderRadius: 8,
                      background: '#fff',
                      cursor: 'pointer'
                    }}
                  />
                )}
                <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                  (Trial Schema Image)
                </Typography>
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  No trial schema image available.
                </Typography>
              </Box>
            )}
          </Box>
        );
      } else if (section.title === '1.3 SCHEDULE OF ACTIVITIES') {
        return (
          <Box
            sx={{
              mb: 3,
              p: 2,
              bgcolor: 'blue.50',
              borderRadius: 1,
              width: '100%',
              minWidth: 0,
              overflow: 'hidden',
            }}
          >
            <PublishedSoaView
              s9SoaTables={s9SoaTables}
              s9PublishedVersion={s9PublishedVersion}
              generatedSoAData={generatedSoAData}
              protocolId={actualProjectId}
              showHeader
            />
          </Box>
        );
      }
      return null;
    };

    return (
      <SectionWrapper
        key={section._id}
        in={true}
        timeout={300 + (index * 100)}
        style={{ transformOrigin: '0 0 0' }}
      >
        <Paper
          ref={(el) => (sectionRefs.current[section._id] = el)}
          sx={{
            mb: 2,
            borderRadius: 2,
            overflow: section.title === '1.3 SCHEDULE OF ACTIVITIES' ? 'visible' : 'hidden',
            position: 'relative',
            transition: 'all 0.3s ease-in-out',
            border: '1px solid',
            borderColor: hasContent ? '#e3f2fd' : '#f5f5f5',
            backgroundColor: hasContent ? '#ffffff' : '#fafafa',
            ...(section.title === '1.3 SCHEDULE OF ACTIVITIES'
              ? { width: '100%', minWidth: 0 }
              : {}),
            '&:hover': {
              boxShadow: hasContent ? '0 4px 12px rgba(0,0,0,0.15)' : '0 2px 8px rgba(0,0,0,0.1)',
              transform: hasContent ? 'translateY(-2px)' : 'none'
            }
          }}
        >
          {/* AI Assistant Button for this section */}
           {hasContent && !readonlyContent && (
            <SectionAssistantButton
              sectionTitle={section.title}
              sectionContent={section.output}
              allSections={sections}
              position="top-right"
              variant="floating"
            />
          )}
          {/* Section Header */}
          <Box
            sx={{
              p: 2,
              backgroundColor: hasContent ? '#f8f9fa' : '#f5f5f5',
              borderBottom: hasContent ? '1px solid #e0e0e0' : 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              '&:hover': {
                backgroundColor: hasContent ? '#e3f2fd' : '#eeeeee'
              }
            }}
            onClick={() => handleSectionToggle(section._id)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
              {/* Section Number */}
              {showSectionNumbers && (
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    backgroundColor: hasContent ? '#1976d2' : '#bdbdbd',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.875rem',
                    fontWeight: 'bold',
                    mr: 2,
                    flexShrink: 0
                  }}
                >
                  {index + 1}
                </Box>
              )}

              {/* Section Title */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: hasContent ? 600 : 400,
                    color: hasContent ? '#2c3e50' : '#666',
                    fontSize: '1rem',
                    lineHeight: 1.4,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {section.title}
                </Typography>
                {!hasContent && (
                  <Typography variant="caption" sx={{ color: '#999' }}>
                    No content available
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Status Chip */}
              <Chip
                icon={status.icon}
                label={status.label}
                size="small"
                color={status.color}
                variant="outlined"
                sx={{ fontSize: '0.75rem' }}
              />

              {/* Bookmark Button */}
              {/* <Tooltip title={isBookmarked ? 'Remove bookmark' : 'Add bookmark'}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleBookmark?.(section._id);
                  }}
                  sx={{ color: isBookmarked ? '#f57c00' : '#666' }}
                >
                  {isBookmarked ? <BookmarkIcon /> : <BookmarkBorderIcon />}
                </IconButton>
              </Tooltip> */}

              {/* Copy Button
              {hasContent && (
                <Tooltip title={isCopied ? 'Copied!' : 'Copy content'}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCopyContent(section._id, section.output);
                    }}
                    sx={{ 
                      color: isCopied ? '#4caf50' : '#666',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {isCopied ? <CheckIcon /> : <CopyIcon />}
                  </IconButton>
                </Tooltip>
              )} */}

              {/* Edit Button */}
              {/* <Tooltip title="Edit section">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditSection(section);
                  }}
                  sx={{ color: '#666' }}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip> */}

              {/* Expand/Collapse Button */}
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSectionToggle(section._id);
                }}
                sx={{
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s ease',
                  color: '#666'
                }}
              >
                <ExpandMoreIcon />
              </IconButton>
            </Box>
          </Box>

                     {/* Section Content */}
           <Collapse in={isExpanded} timeout="auto">
             <Box sx={{ 
               p: 3,
               // Allow horizontal scroll for wide content like SOA table with Keynotes
               ...(section.title === '1.3 SCHEDULE OF ACTIVITIES'
                 ? { overflowX: 'auto', width: '100%', minWidth: 0, maxWidth: '100%' }
                 : {})
             }}>
               {/* Check for special section content first */}
               {renderSpecialSectionContent() || (
                 hasContent ? (
                   <Box sx={{
                     fontFamily: 'Times New Roman, serif',
                     fontSize: '14px',
                     lineHeight: 1.6,
                     color: '#333',
                     '& pre': {
                       backgroundColor: '#f8f9fa',
                       borderRadius: '4px',
                       padding: '12px',
                       overflow: 'auto',
                       fontSize: '13px',
                       fontFamily: 'Monaco, Consolas, monospace'
                     },
                     '& code': {
                       backgroundColor: '#f1f3f4',
                       padding: '2px 4px',
                       borderRadius: '3px',
                       fontSize: '13px',
                       fontFamily: 'Monaco, Consolas, monospace'
                     }
                   }}>
                     {renderMarkdownContent(section.output, section.title, mediaNumberOffsets[index])}
                   </Box>
                 ) : (
                   <Box sx={{
                     textAlign: 'center',
                     py: 4,
                     color: '#999',
                     fontStyle: 'italic'
                   }}>
                     <EmptyIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                     <Typography variant="body2">
                       This section has no content yet.
                        {!readonlyContent && (
                       <Button
                         startIcon={<AddIcon />}
                         onClick={() => handleEditSection(section)}
                         sx={{ ml: 1, textTransform: 'none' }}
                         size="small"
                       >
                         Add content
                       </Button>
                        )}
                     </Typography>
                   </Box>
                 )
               )}
             </Box>
           </Collapse>
        </Paper>
      </SectionWrapper>
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Document Overview */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: '#f8f9fa' }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#2c3e50' }}>
          Document Overview
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {sections.map((section, index) => {
              const hasContent = section.output && section.output.trim();
              return (
                <Chip
                  key={section._id}
                  label={section.title}
                  variant={hasContent ? "filled" : "outlined"}
                  color={hasContent ? "primary" : "default"}
                  size="small"
                  sx={{ 
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: hasContent ? '#1976d2' : '#e0e0e0',
                      transform: 'translateY(-1px)'
                    }
                  }}
                  onClick={() => {
                    const element = sectionRefs.current[section._id];
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }}
                />
              );
            })}
        </Box>
        <Typography variant="body2" sx={{ color: '#666' }}>
          {sections.filter(s => s.output && s.output.trim()).length} of {sections.length} sections have content
        </Typography>
      </Paper>

      {/* Sections Canvas */}
      <Box>
        {sections.map((section, index) => renderSection(section, index))}
      </Box>

      {/* Edit Dialog */}
      <Dialog 
        open={showEditDialog} 
        onClose={handleCancelEdit}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: '70vh',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          borderBottom: '1px solid #e0e0e0'
        }}>
          <Typography variant="h6">
            Edit Section: {sections.find(s => s._id === editingSectionId)?.title}
          </Typography>
          <IconButton onClick={handleCancelEdit} size="small">
            <CancelIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Content Editor */}
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                Content
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={15}
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                variant="outlined"
                placeholder="Enter your content here... (Markdown supported)"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontFamily: 'Monaco, Consolas, monospace',
                    fontSize: '14px'
                  }
                }}
              />
            </Box>

            {/* Prompt Editor */}
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                Prompt (Optional)
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={editPrompt}
                onChange={(e) => setEditPrompt(e.target.value)}
                variant="outlined"
                placeholder="Enter a prompt to guide content generation..."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontFamily: 'Monaco, Consolas, monospace',
                    fontSize: '14px'
                  }
                }}
              />
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, borderTop: '1px solid #e0e0e0' }}>
          <Button 
            onClick={handleCancelEdit}
            variant="outlined"
            startIcon={<CancelIcon />}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveEdit}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={!editContent.trim()}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={showSnackbar}
        autoHideDuration={3000}
        onClose={() => setShowSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setShowSnackbar(false)} 
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DocumentCanvas;
